import type { Link, NumberRange, Resource } from '@readium/shared';
import node_cryptojs from 'node-cryptojs-aes';
import { z } from 'zod/mini';
const { CryptoJS } = node_cryptojs;
const { JsonFormatter } = node_cryptojs;

interface ObjectWithHtml {
  html: string;
}

/** Last change to a decrypted chapter before Readium renders it. */
export type ChapterTransform = (html: string, link: Link) => Promise<string>;

export class CustomHttpResource implements Resource {
  private readonly resource: Resource;
  private readonly cipherKey?: string;
  private readonly transformChapter?: ChapterTransform;

  constructor(resource: Resource, cipherKey?: string, transformChapter?: ChapterTransform) {
    this.resource = resource;
    this.cipherKey = cipherKey;
    this.transformChapter = transformChapter;
  }

  close(): void {
    this.resource.close();
  }

  decryptObject(object: object | string): ObjectWithHtml {
    const encrypted_json_str = typeof object === 'string' ? object : JSON.stringify(object);

    const decrypted = CryptoJS.AES.decrypt(encrypted_json_str, this.cipherKey ?? '', {
      format: JsonFormatter,
    });
    const decrypted_str = CryptoJS.enc.Utf8.stringify(decrypted);
    const ObjectParsed: unknown = JSON.parse(decrypted_str);
    const objWithHtml = z.object({
      html: z.string(),
    });
    return objWithHtml.parse(ObjectParsed);
  }

  async link(): Promise<Link> {
    return this.resource.link();
  }

  async read(range?: NumberRange): Promise<Uint8Array | undefined> {
    return this.resource.read(range);
  }

  async length(): Promise<number | undefined> {
    return this.resource.length();
  }

  async readAsJSON(): Promise<unknown> {
    return this.resource.readAsJSON();
  }

  async readAsString(): Promise<string | undefined> {
    const str = await this.resource.readAsString();
    const decrypted = str && this.decryptObject(str).html;
    if (!decrypted || !this.transformChapter) return decrypted;
    return this.transformChapter(decrypted, await this.resource.link());
  }

  /**
   * Readium builds each chapter frame from this document (since navigator 2.11), so it has to be
   * parsed from the decrypted chapter, the way Readium's own `Resource.readAsXML()` parses it.
   */
  async readAsXML(): Promise<Document | undefined> {
    const str = await this.readAsString();
    if (str === undefined) return undefined;
    const { mediaType } = await this.resource.link();
    const type: DOMParserSupportedType =
      mediaType.essence === 'text/html' ? 'text/html' : 'application/xhtml+xml';
    return new DOMParser().parseFromString(str, mediaType.isHTML ? type : 'text/xml');
  }
}
